import type { Graph, GraphConnection, GraphNode } from './graph.js'

export interface NodeChange {
  id: string
  name: string
  /** Пути изменившихся полей: `parameters.text`, `retry.maxTries`. */
  fields: string[]
}

export interface GraphDiff {
  addedNodes: { id: string; name: string }[]
  removedNodes: { id: string; name: string }[]
  changedNodes: NodeChange[]
  addedConnections: string[]
  removedConnections: string[]
}

/**
 * Положение узла на холсте из сравнения исключено. Перетащить узел — не
 * изменение сценария, а изменение картинки; попади оно в список, каждая
 * версия отличалась бы от предыдущей десятком «изменений», среди которых
 * настоящие не найти.
 */
const IGNORED_FIELDS: ReadonlySet<string> = new Set(['position'])

/** Плоские пути значений: `{a: {b: 1}}` → `{'a.b': 1}`. */
function flatten(value: unknown, prefix = ''): Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return prefix === '' ? {} : { [prefix]: value }
  }

  const flat: Record<string, unknown> = {}
  const entries = Array.isArray(value)
    ? value.map((entry, index) => [String(index), entry] as const)
    : Object.entries(value as Record<string, unknown>)

  if (entries.length === 0) return prefix === '' ? {} : { [prefix]: value }

  for (const [key, nested] of entries) {
    const path = prefix === '' ? key : `${prefix}.${key}`
    Object.assign(flat, flatten(nested, path))
  }
  return flat
}

function rootOf(path: string): string {
  return path.split('.')[0] as string
}

/** Изменившиеся поля узла. Сравнение по значениям, а не по ссылкам. */
function changedFields(before: GraphNode, after: GraphNode): string[] {
  const from = flatten(before)
  const to = flatten(after)
  const paths = [...new Set([...Object.keys(from), ...Object.keys(to)])].sort()

  return paths.filter((path) => {
    if (IGNORED_FIELDS.has(rootOf(path))) return false
    return JSON.stringify(from[path]) !== JSON.stringify(to[path])
  })
}

/** Связь текстом: по именам узлов, а не по идентификаторам. */
function connectionText(connection: GraphConnection, nodes: readonly GraphNode[]): string {
  const name = (id: string): string => nodes.find((node) => node.id === id)?.name ?? id
  return `${name(connection.from.node)} (${connection.from.output}) → ${name(connection.to.node)} (${connection.to.input})`
}

function connectionKey(connection: GraphConnection): string {
  return [
    connection.from.node,
    connection.from.output,
    connection.from.index,
    connection.to.node,
    connection.to.input,
    connection.to.index,
  ].join(' ')
}

/**
 * Чем одна версия отличается от другой (FR-UI-007). Сравнение текстовое, по
 * полям: узлы сопоставляются по идентификатору, а не по положению или
 * имени — переименованный узел это тот же узел, и показывать его как
 * «удалён и добавлен» значит терять то, что в нём поменялось.
 *
 * Считается на клиенте, а не службой: разность двух графов — чистая функция
 * от них, и второй ответ на тот же вопрос завёл бы второе определение слова
 * «изменилось».
 */
export function diffGraphs(from: Graph, to: Graph): GraphDiff {
  const before = new Map(from.nodes.map((node) => [node.id, node]))
  const after = new Map(to.nodes.map((node) => [node.id, node]))

  const addedNodes = to.nodes
    .filter((node) => !before.has(node.id))
    .map((node) => ({ id: node.id, name: node.name }))
  const removedNodes = from.nodes
    .filter((node) => !after.has(node.id))
    .map((node) => ({ id: node.id, name: node.name }))

  const changedNodes: NodeChange[] = []
  for (const node of to.nodes) {
    const previous = before.get(node.id)
    if (previous === undefined) continue
    const fields = changedFields(previous, node)
    if (fields.length > 0) changedNodes.push({ id: node.id, name: node.name, fields })
  }

  const beforeConnections = new Set(from.connections.map(connectionKey))
  const afterConnections = new Set(to.connections.map(connectionKey))

  return {
    addedNodes,
    removedNodes,
    changedNodes,
    addedConnections: to.connections
      .filter((connection) => !beforeConnections.has(connectionKey(connection)))
      .map((connection) => connectionText(connection, to.nodes)),
    removedConnections: from.connections
      .filter((connection) => !afterConnections.has(connectionKey(connection)))
      .map((connection) => connectionText(connection, from.nodes)),
  }
}

/** Различий нет: версии совпадают по существу. */
export function isEmptyDiff(diff: GraphDiff): boolean {
  return (
    diff.addedNodes.length === 0 &&
    diff.removedNodes.length === 0 &&
    diff.changedNodes.length === 0 &&
    diff.addedConnections.length === 0 &&
    diff.removedConnections.length === 0
  )
}
